import { Link } from "react-router-dom";
import { ArrowRight, Check, Play } from "lucide-react";
import { GetImageUrl } from "../../helpers/GetImageUrl";
import { getVideoEmbedUrl } from "../../helpers/videoEmbed";

const getMediaUrl = (media) => GetImageUrl(media?.url || media);

const VideoPlayer = ({
  media,
  className = "aspect-video w-full bg-black object-cover",
}) => {
  const videoUrl = media?.url || media;
  const embedUrl = getVideoEmbedUrl(videoUrl);

  if (embedUrl) {
    return (
      <iframe
        src={embedUrl}
        title={media?.title || "Video"}
        className={className}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
      />
    );
  }

  return (
    <video
      controls
      preload="metadata"
      poster={media?.thumbnail || undefined}
      className={className}
    >
      <source src={getMediaUrl(media)} />
    </video>
  );
};

const ContentSection = ({ content, media, visualIndex }) => {
  const description = content?.description || "";
  const paragraphs = description
    .split(/\n+/)
    .map((paragraph) => paragraph.trim())
    .filter(Boolean);

  const mediaOnRight = visualIndex % 2 === 1;
  const mediaMarkup =
    media?.type === "video" ? (
      <VideoPlayer media={media} />
    ) : (
      <img
        src={getMediaUrl(media)}
        alt={media?.alt || content?.heading || "Security solution"}
        loading="lazy"
        className="w-full h-auto object-contain"
      />
    );

  return (
    <section
      className={`grid items-center gap-8 py-12 lg:grid-cols-2 lg:gap-16 lg:py-16 ${!media ? "lg:grid-cols-1" : ""}`}
    >
      {media && (
        <div className={mediaOnRight ? "lg:order-2" : "lg:order-1"}>
          <div className="relative overflow-hidden rounded-2xl bg-slate-100 shadow-[0_20px_55px_rgba(15,23,42,0.12)]">
            {mediaMarkup}
            <div className="absolute inset-x-0 bottom-0 h-20 bg-linear-to-t from-slate-950/35 to-transparent" />
          </div>
        </div>
      )}

      <div className={mediaOnRight ? "lg:order-1" : "lg:order-2"}>
        <span className="mb-4 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-blue-700">
          <span className="h-px w-8 bg-blue-600" />
          {String(visualIndex).padStart(2, "0")}
        </span>
        {content?.heading && (
          <h2 className="max-w-xl font-serif text-2xl font-bold leading-tight text-slate-950 sm:text-3xl">
            {content.heading}
          </h2>
        )}
        {content?.subHeading && (
          <h3 className="mt-3 max-w-xl text-base font-semibold text-slate-700 sm:text-lg">
            {content.subHeading}
          </h3>
        )}
        <div className="mt-5 max-w-xl space-y-3 text-[15px] leading-7 text-slate-600">
          {paragraphs.map((paragraph, paragraphIndex) => (
            <p key={paragraphIndex}>{paragraph}</p>
          ))}
        </div>
      </div>
    </section>
  );
};

const FeaturesSection = ({ features }) => {
  if (!features?.length) return null;

  return (
    <section className="border-y border-slate-200 bg-slate-50 py-14 lg:py-16">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-9 max-w-2xl">
          <span className="text-xs font-bold uppercase tracking-[0.18em] text-blue-700">
            Built for confidence
          </span>
          <h2 className="mt-3 font-serif text-3xl font-bold text-slate-950 sm:text-4xl">
            What makes it work
          </h2>
        </div>
        <div className="grid gap-5 md:grid-cols-2">
          {features.map((feature, index) => (
            <article
              key={feature?._id || index}
              className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm"
            >
              {feature?.heading && (
                <h3 className="text-xl font-bold text-slate-950">
                  {feature.heading}
                </h3>
              )}
              {feature?.subHeading && (
                <p className="mt-2 font-medium text-blue-700">
                  {feature.subHeading}
                </p>
              )}
              {feature?.description && (
                <p className="mt-3 text-sm leading-6 text-slate-600">
                  {feature.description}
                </p>
              )}
              {feature?.items?.length > 0 && (
                <ul className="mt-5 grid gap-3 sm:grid-cols-2">
                  {feature.items.map((item, itemIndex) => (
                    <li
                      key={item?._id || itemIndex}
                      className="flex gap-2 text-sm leading-6 text-slate-700"
                    >
                      <Check
                        size={18}
                        className="mt-1 shrink-0 text-blue-600"
                      />
                      <span>
                        <strong className="text-slate-950">{item?.key}</strong>
                        {item?.value ? `: ${item.value}` : ""}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

const DynamicFeatureSection = ({ section }) => (
  <section className="border-y border-slate-200 bg-slate-50 py-14 lg:py-16">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      {section.heading && (
        <h2 className="font-serif text-3xl font-bold text-slate-950 sm:text-4xl">
          {section.heading}
        </h2>
      )}
      {section.description && (
        <p className="mt-3 max-w-2xl text-[15px] leading-7 text-slate-600">
          {section.description}
        </p>
      )}
      <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
        {(section.cards || []).map((card, index) => (
          <article
            key={card?._id || index}
            className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm"
          >
            <h3 className="text-lg font-bold text-slate-950">
              {card.title || card.key}
            </h3>
            <p className="mt-2 text-sm leading-6 text-slate-600">
              {card.description || card.value}
            </p>
          </article>
        ))}
      </div>
    </div>
  </section>
);

const GallerySection = ({ galleryImages, title }) => {
  if (!galleryImages?.length) return null;

  return (
    <section className="py-14 lg:py-16">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-8 flex items-end justify-between gap-5">
          <div>
            <span className="text-xs font-bold uppercase tracking-[0.18em] text-blue-700">
              In the field
            </span>
            <h2 className="mt-2 font-serif text-3xl font-bold text-slate-950 sm:text-4xl">
              {title} gallery
            </h2>
          </div>
          <span className="hidden text-sm text-slate-500 sm:block">
            Real installations. Real outcomes.
          </span>
        </div>
        <div className="grid auto-rows-[180px] grid-cols-2 gap-3 sm:auto-rows-[220px] sm:gap-5 lg:grid-cols-4">
          {galleryImages.map((image, index) => (
            <a
              key={image?._id || index}
              href={getMediaUrl(image)}
              target="_blank"
              rel="noreferrer"
              className={`group relative overflow-hidden rounded-2xl bg-slate-100 ${index % 5 === 0 ? "col-span-2 row-span-2" : ""}`}
            >
              <img
                src={getMediaUrl(image)}
                alt={image?.alt || title}
                loading="lazy"
                className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
              />
              <span className="absolute inset-0 bg-linear-to-t from-slate-950/45 via-transparent to-transparent opacity-0 transition group-hover:opacity-100" />
            </a>
          ))}
        </div>
      </div>
    </section>
  );
};

const VideoSection = ({ videos }) => {
  if (!videos?.length) return null;

  return (
    <section className="bg-slate-950 py-14 text-white lg:py-16">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-8 flex items-end justify-between gap-5">
          <div>
            <span className="text-xs font-bold uppercase tracking-[0.18em] text-cyan-300">
              See it in action
            </span>
            <h2 className="mt-2 font-serif text-3xl font-bold sm:text-4xl">
              Watch the difference
            </h2>
          </div>
          <Play className="hidden text-cyan-300 sm:block" size={34} />
        </div>
        <div className="grid gap-6 lg:grid-cols-2">
          {videos.map((video, index) => (
            <article
              key={video?._id || index}
              className="overflow-hidden rounded-2xl border border-white/10 bg-white/5"
            >
              <VideoPlayer
                media={video}
                className="aspect-video w-full bg-black"
              />
              {(video?.title || video?.description) && (
                <div className="p-5">
                  <h3 className="text-lg font-bold">{video.title}</h3>
                  <p className="mt-2 text-sm leading-6 text-slate-300">
                    {video.description}
                  </p>
                </div>
              )}
            </article>
          ))}
        </div>
      </div>
    </section>
  );
};

const FaqSection = ({ faq }) => {
  if (!faq?.length) return null;

  return (
    <section className="bg-white py-14 lg:py-16">
      <div className="mx-auto w-full px-4 sm:px-6 lg:w-4/5">
        <div className="mb-8 text-center">
          <span className="text-xs font-bold uppercase tracking-[0.18em] text-blue-700">
            Need clarity?
          </span>
          <h2 className="mt-2 font-serif text-3xl font-bold text-slate-950 sm:text-4xl">
            Frequently Asked Questions
          </h2>
        </div>
        <div className="space-y-3">
          {faq.map((item, index) => (
            <details
              key={item?._id || index}
              className="group rounded-xl border border-slate-200 bg-slate-50 px-5 py-4"
            >
              <summary className="flex cursor-pointer list-none items-center justify-between gap-5 font-semibold text-slate-900">
                {item?.ques}
                <span className="text-xl text-blue-600 transition group-open:rotate-45">
                  +
                </span>
              </summary>
              <p className="mt-3 max-w-2xl text-sm leading-6 text-slate-600">
                {item?.ans}
              </p>
            </details>
          ))}
        </div>
      </div>
    </section>
  );
};

export default function MediaRichDetail({ item, type }) {
  const galleryImages = item?.galleryImages || [];
  const legacySections = [
    ...(item?.contents || []).map((content) => ({
      ...content,
      type: "content",
      cards: content.cards || [],
    })),
    ...(item?.features || []).map((feature) => ({
      type: "features",
      heading: feature.heading,
      subHeading: feature.subHeading,
      description: feature.description,
      cards: (feature.items || []).map((entry) => ({
        title: entry.key,
        description: entry.value,
        key: entry.key,
        value: entry.value,
      })),
    })),
  ];
  const sections = (item?.sections?.length ? item.sections : legacySections)
    .map((section, index) => ({ ...section, order: section.order ?? index }))
    .sort((first, second) => first.order - second.order);
  const contents = sections.filter((section) => section.type === "content");
  const fallbackImage = item?.sectionImage
    ? { type: "image", url: item.sectionImage, alt: item.sectionAlt }
    : null;
  const usedGalleryIds = new Set();
  const contentMedia = contents.map((content, index) => {
    if (content?.video?.url) return { type: "video", ...content.video };
    if (content?.image?.url) return { type: "image", ...content.image };
    const fallback = galleryImages.find(
      (image) => !usedGalleryIds.has(image?._id || image?.publicId),
    );
    if (fallback) usedGalleryIds.add(fallback?._id || fallback?.publicId);
    return fallback
      ? { type: "image", ...fallback }
      : index === 0
        ? fallbackImage
        : null;
  });
  const title = type === "service" ? "service" : "solution";

  return (
    <>
      <section className="w-full overflow-hidden">
        {item?.bannerImage && (
          <img
            src={GetImageUrl(item.bannerImage)}
            alt={item?.bannerAlt || item?.bannerTitle || "Banner"}
            className="block w-full h-auto"
          />
        )}
      </section>

      <main>
        <section className="bg-white py-12 lg:py-16">
          <div className="mx-auto grid max-w-7xl items-center gap-8 px-4 sm:px-6 lg:grid-cols-2 lg:gap-16 lg:px-8">
            <div className="overflow-hidden rounded-2xl bg-slate-100 shadow-[0_20px_55px_rgba(15,23,42,0.12)]">
              <img
                src={GetImageUrl(item?.sectionImage)}
                alt={item?.sectionAlt || item?.bannerTitle}
                className="w-full h-auto object-contain"
              />
            </div>
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.18em] text-blue-700">
                {item?.introductionLabel || `About this ${title}`}
              </span>
              <h2 className="mt-3 font-serif text-3xl font-bold text-slate-950 sm:text-4xl">
                {item?.introductionHeading ||
                  item?.contents?.[0]?.heading ||
                  item?.bannerTitle}
              </h2>
              <p className="mt-5 max-w-xl text-[15px] leading-7 text-slate-600">
                {item?.introductionDescription ||
                  item?.contents?.[0]?.description ||
                  item?.bannerSubtitle}
              </p>
            </div>
          </div>
        </section>

        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {sections.map((section, index) =>
            section.type === "content" ? (
              <ContentSection
                key={section?._id || index}
                content={section}
                media={contentMedia[contents.indexOf(section)]}
                visualIndex={contents.indexOf(section) + 1}
              />
            ) : (
              <DynamicFeatureSection
                key={section?._id || index}
                section={section}
              />
            ),
          )}
        </div>
        <VideoSection videos={item?.videos || []} />
        <GallerySection
          galleryImages={galleryImages}
          title={item?.bannerTitle || title}
        />
        <FaqSection faq={item?.faq || []} />
        {(item?.cta?.heading || item?.cta?.description || item?.cta?.label) && (
          <section className="bg-blue-700 py-14 text-white">
            <div className="mx-auto flex max-w-7xl flex-col items-start justify-between gap-6 px-4 sm:px-6 md:flex-row md:items-center lg:px-8">
              <div>
                <h2 className="font-serif text-3xl font-bold sm:text-4xl">
                  {item.cta.heading}
                </h2>
                {item.cta.description && (
                  <p className="mt-3 max-w-2xl text-blue-100">
                    {item.cta.description}
                  </p>
                )}
              </div>
              {item.cta.label && (
                <Link
                  to={item.cta.url || "/contact"}
                  className="inline-flex shrink-0 items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-bold text-blue-700 transition hover:bg-cyan-200"
                >
                  {item.cta.label} <ArrowRight size={17} />
                </Link>
              )}
            </div>
          </section>
        )}
      </main>
    </>
  );
}
